import { useState, useCallback } from "react";

// ─── Constants ───────────────────────────────────────────────────────────────
const MEDICAL_INSURANCE = 510;
const SI_MIN = 2700, SI_MAX = 16700;
const PERSONAL_EXEMPTION_ANNUAL = 20000;

function calculateNetFromGross(insuranceWage, allowances) {
  const totalGross = insuranceWage + allowances;
  const siWage = Math.max(SI_MIN, Math.min(insuranceWage, SI_MAX));
  const socialInsurance = siWage * 0.11;
  const personalExemptionMonthly = PERSONAL_EXEMPTION_ANNUAL / 12;
  const taxableMonthly = Math.max(0, totalGross - socialInsurance - personalExemptionMonthly);
  const taxableAnnual = taxableMonthly * 12;

  let brackets;
  if (taxableAnnual <= 600000)
    brackets = [[40000,0],[15000,.10],[15000,.15],[130000,.20],[200000,.225],[800000,.25],[Infinity,.275]];
  else if (taxableAnnual <= 700000)
    brackets = [[55000,.10],[15000,.15],[130000,.20],[200000,.225],[800000,.25],[Infinity,.275]];
  else if (taxableAnnual <= 800000)
    brackets = [[70000,.15],[130000,.20],[200000,.225],[800000,.25],[Infinity,.275]];
  else if (taxableAnnual <= 900000)
    brackets = [[200000,.20],[200000,.225],[800000,.25],[Infinity,.275]];
  else if (taxableAnnual <= 1200000)
    brackets = [[400000,.225],[800000,.25],[Infinity,.275]];
  else
    brackets = [[1200000,.25],[Infinity,.275]];

  let annualTax = 0, temp = taxableAnnual;
  for (const [limit, rate] of brackets) {
    if (temp <= 0) break;
    annualTax += Math.min(temp, limit) * rate;
    temp -= Math.min(temp, limit);
  }
  const monthlyTax = annualTax / 12;
  const net = totalGross - socialInsurance - MEDICAL_INSURANCE - monthlyTax;

  return {
    insuranceWage: round(insuranceWage),
    allowances: round(allowances),
    totalGross: round(totalGross),
    socialInsurance: round(socialInsurance),
    medicalInsurance: MEDICAL_INSURANCE,
    personalExemption: round(personalExemptionMonthly),
    taxableMonthly: round(taxableMonthly),
    taxableAnnual: round(taxableAnnual),
    incomeTax: round(monthlyTax),
    net: round(net),
  };
}

function findGrossFromNet(targetNet, tol = 0.5) {
  let lo = targetNet, hi = targetNet * 3;
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    const iw = Math.min(mid, SI_MAX);
    const al = Math.max(0, mid - SI_MAX);
    const { net } = calculateNetFromGross(iw, al);
    if (Math.abs(net - targetNet) < tol) return mid;
    if (net < targetNet) lo = mid; else hi = mid;
  }
  return (lo + hi) / 2;
}

function round(v) { return Math.round(v * 100) / 100; }
function fmt(v) { return v.toLocaleString("en-EG", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

const GOLD = "#C9A84C";
const DARK = "#0E1117";
const CARD = "#161B27";
const BORDER = "#1F2A3C";
const SUBTLE = "#0A0E16";

const deductionConfig = [
  { key: "socialInsurance",  label: "Social Insurance",  labelAr: "حصة العامل في التأمينات", sub: "11% of insurance wage (capped 2,700–16,700)", color: "#E05C5C" },
  { key: "medicalInsurance", label: "Medical Insurance", labelAr: "تأمين طبي",                sub: "Fixed 510 EGP / month",                      color: "#5CB8E0" },
  { key: "incomeTax",        label: "Income Tax",        labelAr: "ضريبة الدخل",              sub: "Progressive brackets 2026",                   color: "#7B8DE0" },
];

function NumInput({ label, labelAr, value, onChange, placeholder, hint }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
        <span style={{ fontSize: 11, letterSpacing: 2, color: "#7A8899", fontFamily: "monospace" }}>{label}</span>
        <span style={{ fontSize: 12, color: "#4A5F72", fontFamily: "Arial, sans-serif", direction: "rtl" }}>{labelAr}</span>
      </label>
      <input
        type="number"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: "100%", boxSizing: "border-box",
          background: SUBTLE, border: `1px solid ${BORDER}`,
          borderRadius: 2, padding: "12px 16px", fontSize: 20,
          color: "#F0EAD6", outline: "none", fontFamily: "Georgia, serif",
        }}
      />
      {hint && <div style={{ fontSize: 10, color: "#3A4A5A", fontFamily: "monospace", marginTop: 5 }}>{hint}</div>}
    </div>
  );
}

export default function App() {
  const [mode, setMode] = useState("gross");
  const [insWage, setInsWage] = useState("");
  const [allowances, setAllowances] = useState("");
  const [netInput, setNetInput] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculate = useCallback(() => {
    setError("");
    if (mode === "gross") {
      const iw = parseFloat(insWage) || 0;
      const al = parseFloat(allowances) || 0;
      if (iw <= 0) { setError("Insurance wage must be greater than 0."); setResult(null); return; }
      setResult(calculateNetFromGross(iw, al));
    } else {
      const net = parseFloat(netInput);
      if (!net || net <= 0) { setError("Please enter a valid net amount."); setResult(null); return; }
      const totalGross = findGrossFromNet(net);
      const iw = Math.min(totalGross, SI_MAX);
      const al = Math.max(0, totalGross - SI_MAX);
      setResult(calculateNetFromGross(iw, al));
    }
  }, [mode, insWage, allowances, netInput]);

  const switchMode = (m) => { setMode(m); setResult(null); setError(""); setInsWage(""); setAllowances(""); setNetInput(""); };
  const totalDeductions = result ? result.socialInsurance + result.medicalInsurance + result.incomeTax : 0;
  const effectiveRate = result ? ((totalDeductions / result.totalGross) * 100).toFixed(1) : 0;
  const liveTotal = (parseFloat(insWage) || 0) + (parseFloat(allowances) || 0);

  return (
    <div style={{
      minHeight: "100vh", background: DARK, color: "#E8EAF0",
      fontFamily: "'Georgia', 'Times New Roman', serif",
      display: "flex", flexDirection: "column", alignItems: "center",
      padding: "48px 16px 80px",
    }}>
      <div style={{ textAlign: "center", marginBottom: 40 }}>
        <div style={{ fontSize: 11, letterSpacing: 6, color: GOLD, marginBottom: 8, fontFamily: "monospace" }}>ARAB REPUBLIC OF EGYPT · 2026</div>
        <div style={{ fontSize: 13, color: "#3A5060", fontFamily: "Arial, sans-serif", direction: "rtl", marginBottom: 10 }}>تسوية ضريبة المرتبات</div>
        <h1 style={{ margin: 0, fontSize: "clamp(24px, 5vw, 38px)", fontWeight: 400, color: "#F0EAD6", letterSpacing: 1 }}>Salary Calculator</h1>
        <div style={{ width: 60, height: 2, background: GOLD, margin: "14px auto 0" }} />
      </div>

      <div style={{
        width: "100%", maxWidth: 580, background: CARD,
        border: `1px solid ${BORDER}`, borderRadius: 2, padding: "36px 32px",
        boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
      }}>
        <div style={{ display: "flex", marginBottom: 30, borderRadius: 2, overflow: "hidden", border: `1px solid ${BORDER}` }}>
          {[["gross", "Gross → Net"], ["net", "Net → Gross"]].map(([m, label]) => (
            <button key={m} onClick={() => switchMode(m)} style={{
              flex: 1, padding: "12px 0", border: "none", cursor: "pointer",
              background: mode === m ? GOLD : "transparent",
              color: mode === m ? DARK : "#8899AA",
              fontFamily: "monospace", fontSize: 13, letterSpacing: 1,
              fontWeight: mode === m ? 700 : 400, transition: "all .2s",
            }}>{label}</button>
          ))}
        </div>

        {mode === "gross" ? (<>
          <NumInput label="INSURANCE WAGE" labelAr="أجر تأميني شهري" value={insWage} onChange={setInsWage}
            placeholder="e.g. 16700" hint="SI base — capped between 2,700 and 16,700 EGP" />
          <NumInput label="ALLOWANCES & BONUSES" labelAr="حوافز وبدلات" value={allowances} onChange={setAllowances}
            placeholder="e.g. 10300" hint="Added to insurance wage → total gross & taxable income" />
          {liveTotal > 0 && (
            <div style={{ marginBottom: 18, padding: "10px 16px", background: SUBTLE, border: `1px solid ${BORDER}`, borderRadius: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 11, color: "#5A6677", fontFamily: "monospace", letterSpacing: 2 }}>TOTAL GROSS  <span style={{ fontFamily: "Arial", direction: "rtl", fontSize: 11 }}>إجمالي الاستحقاقات</span></span>
              <span style={{ fontSize: 17, color: "#F0EAD6" }}>{fmt(liveTotal)} EGP</span>
            </div>
          )}
        </>) : (
          <NumInput label="NET MONTHLY SALARY" labelAr="صافي المرتب" value={netInput} onChange={setNetInput}
            placeholder="e.g. 21162" hint="Assumes insurance wage is capped at 16,700 — adjust inputs manually if different" />
        )}

        {error && <div style={{ color: "#E05C5C", fontSize: 12, marginBottom: 14, fontFamily: "monospace" }}>{error}</div>}

        <button onClick={calculate} style={{
          width: "100%", padding: "14px 0", background: GOLD, border: "none",
          borderRadius: 2, cursor: "pointer", color: DARK,
          fontFamily: "monospace", fontSize: 14, fontWeight: 700,
          letterSpacing: 3, marginBottom: result ? 28 : 0, transition: "opacity .15s",
        }}
          onMouseEnter={e => e.target.style.opacity = ".8"}
          onMouseLeave={e => e.target.style.opacity = "1"}>
          CALCULATE
        </button>

        {result && (
          <div style={{ animation: "fadeIn .35s ease" }}>
            <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 22 }}>
              {[
                { label: "TOTAL GROSS", labelAr: "إجمالي الاستحقاقات", value: result.totalGross, accent: false },
                { label: "NET SALARY",  labelAr: "صافي المرتب",         value: result.net,        accent: true  },
              ].map(({ label, labelAr, value, accent }) => (
                <div key={label} style={{
                  background: accent ? "rgba(201,168,76,0.08)" : SUBTLE,
                  border: `1px solid ${accent ? GOLD : BORDER}`,
                  borderRadius: 2, padding: "18px 14px", textAlign: "center",
                }}>
                  <div style={{ fontSize: 9, letterSpacing: 3, color: accent ? GOLD : "#5A6677", fontFamily: "monospace", marginBottom: 3 }}>{label}</div>
                  <div style={{ fontSize: 10, color: "#3A4A5A", fontFamily: "Arial", direction: "rtl", marginBottom: 8 }}>{labelAr}</div>
                  <div style={{ fontSize: "clamp(16px, 3.5vw, 24px)", color: accent ? GOLD : "#F0EAD6" }}>{fmt(value)}</div>
                  <div style={{ fontSize: 10, color: "#4A5566", fontFamily: "monospace", marginTop: 4 }}>EGP / month</div>
                </div>
              ))}
            </div>

            <div style={{ background: SUBTLE, border: `1px solid ${BORDER}`, borderRadius: 2, padding: "14px 16px", marginBottom: 20 }}>
              <div style={{ fontSize: 10, letterSpacing: 3, color: "#5A6677", fontFamily: "monospace", marginBottom: 12 }}>GROSS COMPONENTS</div>
              {[
                { label: "Insurance Wage", labelAr: "أجر تأميني", value: result.insuranceWage, note: `SI base (cap: ${fmt(SI_MAX)})` },
                { label: "Allowances", labelAr: "حوافز وبدلات", value: result.allowances, note: "Not subject to SI" },
              ].map(({ label, labelAr, value, note }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div>
                    <span style={{ fontSize: 12, color: "#B0BCC8" }}>{label}</span>
                    <span style={{ fontSize: 11, color: "#3A4A5A", marginLeft: 8, fontFamily: "Arial" }}>{labelAr}</span>
                    <div style={{ fontSize: 10, color: "#3A4A5A", fontFamily: "monospace" }}>{note}</div>
                  </div>
                  <span style={{ fontSize: 14, color: "#D0D8E4" }}>{fmt(value)}</span>
                </div>
              ))}
              <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 10, marginTop: 2 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 10, color: "#5A6677", fontFamily: "monospace" }}>PERSONAL EXEMPTION (monthly) <span style={{ fontFamily: "Arial" }}>إعفاء شخصي</span></span>
                  <span style={{ fontSize: 12, color: "#5A6677" }}>{fmt(result.personalExemption)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 10, color: "#7A8899", fontFamily: "monospace" }}>TAXABLE BASE (monthly) <span style={{ fontFamily: "Arial" }}>وعاء الضريبة</span></span>
                  <span style={{ fontSize: 13, color: "#C0CCD8" }}>{fmt(result.taxableMonthly)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 10, color: "#7A8899", fontFamily: "monospace" }}>TAXABLE BASE (annual)</span>
                  <span style={{ fontSize: 13, color: "#C0CCD8" }}>{fmt(result.taxableAnnual)}</span>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 10, letterSpacing: 4, color: "#5A6677", fontFamily: "monospace", marginBottom: 14 }}>DEDUCTIONS  الإستقطاعات</div>
              {deductionConfig.map(({ key, label, labelAr, sub, color }) => {
                const val = result[key];
                const pct = ((val / result.totalGross) * 100).toFixed(2);
                const barW = ((val / totalDeductions) * 100).toFixed(1);
                return (
                  <div key={key} style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 5 }}>
                      <div>
                        <span style={{ fontSize: 13, color: "#D0D8E4" }}>{label}</span>
                        <span style={{ fontSize: 11, color: "#3A4A5A", marginLeft: 8, fontFamily: "Arial", direction: "rtl" }}>{labelAr}</span>
                        <div style={{ fontSize: 10, color: "#4A5566", fontFamily: "monospace" }}>{sub}</div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
                        <span style={{ fontSize: 14, color: "#D0D8E4" }}>{fmt(val)}</span>
                        <span style={{ fontSize: 10, color: "#4A5566", marginLeft: 6, fontFamily: "monospace" }}>{pct}%</span>
                      </div>
                    </div>
                    <div style={{ height: 3, background: "#0A0E16", borderRadius: 2 }}>
                      <div style={{ height: "100%", width: `${barW}%`, background: color, borderRadius: 2, transition: "width .6s ease" }} />
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 18, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 10, letterSpacing: 3, color: "#5A6677", fontFamily: "monospace" }}>TOTAL DEDUCTIONS  جملة الإستقطاعات</div>
                <div style={{ fontSize: 18, color: "#E05C5C", marginTop: 4 }}>{fmt(totalDeductions)} EGP</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 10, letterSpacing: 3, color: "#5A6677", fontFamily: "monospace" }}>EFFECTIVE RATE</div>
                <div style={{ fontSize: 18, color: GOLD, marginTop: 4 }}>{effectiveRate}%</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{ marginTop: 32, fontSize: 10, color: "#2A3444", fontFamily: "monospace", letterSpacing: 2, textAlign: "center" }}>
        EGYPT INCOME TAX LAW · 2026 · AB CONCEPT PAYROLL STRUCTURE
      </div>
    </div>
  );
}
